import React, { useEffect, useState } from "react"
import { Navigate, Route, Routes, useParams } from "react-router-dom"

import { Loading } from "components"

import HelpBreadcrumbs from "./HelpBreadcrumbs"
import HelpPage from "./HelpPage"
import HelpSidebar from "./HelpSidebar"
import HelpTopbar from "./HelpTopbar"
import {
  findFirstPage,
  loadSitemap,
  useAvailableAreas,
} from "./helpUtils"

import "./index.scss"

export default function HelpScreen() {
  const availableAreas = useAvailableAreas()
  const [sitemap, setSitemap] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    loadSitemap()
      .then((data) => {
        if (!cancelled) setSitemap(data)
      })
      .catch((err) => {
        if (!cancelled) setError(err)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (error) {
    return (
      <div className="HelpScreen HelpScreen-fallback">
        <p>Help index failed to load.</p>
      </div>
    )
  }

  if (!sitemap) {
    return (
      <div className="HelpScreen HelpScreen-fallback">
        <Loading />
      </div>
    )
  }

  return (
    <Routes>
      <Route
        index
        element={<HelpAreaRedirect sitemap={sitemap} availableAreas={availableAreas} />}
      />
      <Route
        path=":area"
        element={<HelpAreaRedirect sitemap={sitemap} availableAreas={availableAreas} />}
      />
      <Route
        path=":area/:page"
        element={<HelpLayout sitemap={sitemap} availableAreas={availableAreas} />}
      />
      <Route path="*" element={<HelpNotFound />} />
    </Routes>
  )
}

function HelpAreaRedirect({ sitemap, availableAreas }) {
  const { area } = useParams()
  const targetArea =
    area && availableAreas.includes(area) ? area : availableAreas[0]

  if (!targetArea) return <HelpNoAccess />

  const firstPage = findFirstPage(sitemap, targetArea)
  if (!firstPage) return <HelpEmptyArea area={targetArea} />

  return <Navigate to={`/help/${targetArea}/${firstPage.slug}`} replace />
}

function HelpLayout({ sitemap, availableAreas }) {
  const { area, page } = useParams()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  if (!availableAreas.includes(area)) {
    return <HelpAreaRedirect sitemap={sitemap} availableAreas={availableAreas} />
  }

  const closeSidebar = () => setSidebarOpen(false)

  return (
    <div className="HelpScreen">
      <HelpTopbar
        onToggleSidebar={() => setSidebarOpen((v) => !v)}
        sidebarOpen={sidebarOpen}
        sitemap={sitemap}
        availableAreas={availableAreas}
      />
      <div className="HelpScreen-body">
        <HelpSidebar
          sitemap={sitemap}
          availableAreas={availableAreas}
          isOpen={sidebarOpen}
          onNavigate={closeSidebar}
        />
        <main className="HelpScreen-content">
          <HelpBreadcrumbs area={area} slug={page} sitemap={sitemap} />
          <HelpPage area={area} slug={page} />
        </main>
      </div>
    </div>
  )
}

function HelpNotFound() {
  return (
    <div className="HelpScreen HelpScreen-fallback">
      <h1>Page not found</h1>
      <p>The help page you requested does not exist.</p>
    </div>
  )
}

function HelpNoAccess() {
  return (
    <div className="HelpScreen HelpScreen-fallback">
      <h1>No help available</h1>
      <p>Your account does not currently have any help areas enabled.</p>
    </div>
  )
}

function HelpEmptyArea({ area }) {
  return (
    <div className="HelpScreen HelpScreen-fallback">
      <h1>No pages yet</h1>
      <p>
        The <strong>{area}</strong> help area has no pages yet.
      </p>
    </div>
  )
}
